import React, { useState, useEffect } from "react";
import MasterTable from "../../../components/common/MasterTable";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import Loader from "../../../components/Loader/Loader";
import { format, isValid, parse } from "date-fns";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import DeliveryBoyModal from "../DeliveryBoyModal";
import SingleOtpModal from "./SingleOtpModal";
import AcceptOtpModal from "../../../components/CourierBag/AcceptOtpModal";
import AddressDetailModal from "../NotificationMasterHub/AddressDetailModal";
import AddItemBagModal from "../../../components/booking/AddItemBagModal";
import NotificationMasterFilterModal from "./NotificationMasterFilterModal";
import SelectToNotifyModal from "../../../components/booking/SelectToNotify";
import FilterBar from "../../../components/common/FilterBar";

const NOTIFICATION_STATUS_OPTIONS = [
  { value: "NOTIFIED_HUB", label: "Notified Hub" },
  { value: "ARRIVED_AT_HUB", label: "Arrived at Hub" },
  { value: "OPENED_AT_HUB", label: "Opened at Hub" },
  { value: "ALLOCATED_TO_DELIVERY_BOY", label: "Allocated to Delivery Boy" },
  { value: "HANDED_OVER_TO_DELIVERY_BOY", label: "Handed Over to Delivery Boy" },
];

// Helper function to get date 7 days ago in YYYY-MM-DD format
const getLast7DaysDate = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY for API
const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

const defaultFilterData = {
  bagNo: "",
  fromDate: getLast7DaysDate(),
  toDate: getTodayDate(),
  status: "",
  search: "",
};

const orderHeader = [
  "Products",
  "Status",
  "LMBP Id",
  "LMBP Name",
  "Consignee Name",
  "Mobile No",
  "Phone No",
  "Email Id",
  "Address Line 1",
  "Address Line 2",
  "Address Type",
  "Pincode",
  "City",
  "State",
  "Country",
  "Invoice Number",
  "Payment Mode",
  "Express Type",
  "Order Amount (₹)",
  "COD Amount (₹)",
  "Shipment Weight",
  "Shipment Length",
  "Shipment Width",
  "Shipment Height",
  "Pick Address ID",
  "Return Address ID",
  "Seller ID",
  "E-Way Bill Number",
  "Route Code",
  "Carrier",
  "Created Date",
  "Response",
  "Waybill",
];

const NotificationMaster = () => {
  const [pageSize, setPageSize] = useState(10);
  const [noData, setNoData] = useState(false);
  const [editData, setEditData] = useState({});
  const [data, setData] = useState([]);
  const [otpData, setOtpData] = useState(null);
  const [doubleOtpData, setDoubleOtpData] = useState(null);
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hubLoading, setHubLoading] = useState(false);
  const [pageDetails, setPageDetails] = useState(null);
  const loggedInUserName = sessionStorage.getItem("username");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [columnData, setColumnData] = useState("");
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState(null);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [doubleOtpModalOpen, setDoubleOtpModalOpen] = useState(false);
  const [isAddressDetailModalOpen, setIsAddressDetailModalOpen] =
    useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [autoSelectAll, setAutoSelectAll] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [pageDetailsModal, setPageDetailsModal] = useState(null);
  const [orderData, setOrderData] = useState([]);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [acceptHub, setAcceptHub] = useState(false);
  const [openToNotifyModal, setOpenToNotifyModal] = useState(false);

  useEffect(() => {
    fetchNotificationData(1, pageSize, defaultFilterData);
  }, []);

  // fetch notification data
  const fetchNotificationData = async (
    page,
    size = pageSize,
    filters = filterData,
  ) => {
    setLoading(true);

    try {
      const requestBody = {
        username: loggedInUserName,
      };

      // Apply filters if they exist
      if (filters?.bagNo?.trim()) {
        requestBody.bagNo = filters.bagNo.trim();
      }

      // Dates as shown on the filter bar; with the date chip removed, all dates
      if (filters?.fromDate && filters?.toDate) {
        requestBody.fromDate = formatDateForAPI(filters.fromDate);
        requestBody.toDate = formatDateForAPI(filters.toDate);
      }

      if (filters?.search?.trim()) {
        requestBody.search = filters.search.trim();
      }

      if (filters?.status) {
        requestBody.statusList = [filters.status];
      }

      const response = await axiosPostApi(
        `${apiList.FETCH_NOTIFICATION_MASTER}?&pageSize=${size}&pageNo=${page}`,
        requestBody,
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setLoading(false);
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setData(modifiedData);
        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setLoading(false);
        setNoData(true);
        setData([]);
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((user, index) => {
      let formattedCreDate = null;
      let formattedStrDate = null;

      if (user?.createdDate) {
        const parsed = parse(
          user.createdDate,
          "dd-MM-yyyy HH:mm:ss",
          new Date(),
        );
        if (isValid(parsed)) {
          formattedCreDate = parsed;
        } else {
          console.warn("Invalid createdDate:", user.createdDate);
        }
      }

      if (user?.startDateTime) {
        const parsed = parse(
          user.startDateTime,
          "dd-MM-yyyy HH:mm:ss",
          new Date(),
        );
        if (isValid(parsed)) {
          formattedStrDate = parsed;
        } else {
          console.warn("Invalid startDateTime:", user.startDateTime);
        }
      }

      const itemCount = user.itemsIncluded?.length || 0;

      const itemsIncludedIds = Array.isArray(user.itemsIncluded)
        ? user.itemsIncluded
        : [];

      return {
        ...user,

        itemsIncludedIds,

        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",

        startDateTime: formattedStrDate
          ? [
              format(formattedStrDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedStrDate, "HH:mm:ss"),
            ]
          : "",

        itemsIncluded: (
          <span
            className="text-blue-600 underline cursor-pointer"
            onClick={() => handleItemsIncludedClick(user)}
          >
            {itemCount > 0
              ? `${itemCount} Item${itemCount > 1 ? "s" : ""}`
              : "No Items"}
          </span>
        ),

        creatorDetailsMobile: user?.creatorDetails?.mobileNo || "",
      };
    });
  };

  const handleItemsIncludedClick = (row) => {
    setEditData(row);
    setColumnData(row);
    setIsAddItemModalOpen(true);
    fetchOrderData(1, "ItemsIncluded", row);
    setAutoSelectAll(true);
  };

  // fetch order data for add items modal
  const fetchOrderData = async (currentPage, flag, row) => {
    setLoadingModal(true);

    try {
      const requestBody = {
        username: loggedInUserName,
        ...(flag === "addItem"
          ? { excludeBaggedOrders: true }
          : { itemsIncluded: row?.itemsIncluded || [] }),
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_ORDER_MASTER_DATA}?pageSize=${pageSize}&pageNo=${currentPage}`,
        requestBody,
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetailsModal(response?.data?.pageDetails);
        const modifiedData = modifyStructureForModal(response?.data?.data);
        setOrderData(modifiedData);
      } else if (response?.data?.respCode === "01") {
        setOrderData([]);
      } else {
        setOrderData([]);
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "Failed to fetch data",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      setOrderData([]);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoadingModal(false);
    }
  };

  const modifyStructureForModal = (response) => {
    const modified = response?.map((user, index) => {
      const formattedCreDate = user?.createdDate
        ? parse(user.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;
      return {
        ...user,
        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",
      };
    });
    return modified;
  };

  const customerHeader = [
    "Bag No",
    "Status",
    "Items Included",
    "Created Date",
    "Expected Date",
    "Remarks",
  ];

  const customerHeaderToKeyMap = {
    "Bag No": "bagNo",
    Status: "status",
    "Items Included": "itemsIncluded",
    "Created Date": "createdDate",
    "Expected Date": "startDateTime",
    Remarks: "remarks",
  };

  const handleAuthorization = (data, actionType = "view") => {
    console.log("::data::", data);
  };

  const handleAction = (data, actionType = "view") => {
    setModalAction(actionType);
    setColumnData(data);

    if (actionType === "assign") {
      setDeliveryModalOpen(true);
      setEditData(data);
    } else if (actionType === "acceptOtp") {
      setEditData(data);
      setColumnData(data);
      handelDoubleSendOtp(data);
    } else if (actionType === "acceptOtpHub") {
      setEditData(data);
      setColumnData(data);
      handelDoubleSendOtp(data);
      setAcceptHub(true);
    } else if (actionType === "open") {
      setEditData(data);
      setColumnData(data);
      handleOpenBox(data);
    } else if (actionType === "address") {
      setEditData(data);
      setColumnData(data);
      setIsAddressDetailModalOpen(true);
    } else if(actionType === "model"){
      setOpenToNotifyModal(true);
    }else {
      setEditData(data);
    }
  };

  const handelSendOtp = async (selected) => {
    setLoading(true);

    try {
      const requestBody = {
        username: loggedInUserName,
        mobileNo: selected?.mobileNo,
      };

      const response = await axiosPostApi(
        `${apiList.NOTIFICATION_SEND_OTP}`,
        requestBody,
      );
      const data = response?.data?.data;
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setOtpData(data);
        setLoading(false);
        setDeliveryModalOpen(false);
        setOtpModalOpen(true);
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeliverySubmit = (selectedBoy) => {
    handelSendOtp(selectedBoy);
    setSelectedDeliveryBoy(selectedBoy);
  };

  const handelVerifyOtp = async (vOtpData) => {
    setLoading(true);

    try {
      const payload = {
        username: loggedInUserName,
        mobileNo: selectedDeliveryBoy?.mobileNo,
        otp: vOtpData?.sender,
      };

      const response = await axiosPostApi(
        `${apiList.NOTIFICATION_VERIFY_OTP}`,
        payload,
      );
      const data = response?.data?.data;
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setOtpModalOpen(false);
        handelAcceptedByHub();
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handelAcceptedByHub = async () => {
    setHubLoading(true);

    try {
      const payload = {
        username: loggedInUserName,
        bagNo: columnData?.bagNo,
        deliveryBoyId: selectedDeliveryBoy?.id,
      };

      const response = await axiosPostApi(`${apiList.ACCEPT_BY_HUB}`, payload);
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text: response?.data?.message,
          icon: "success",
        });
        setDeliveryModalOpen(false);
        setHubLoading(false);
        fetchNotificationData(currentPage, pageSize, filterData);
      } else {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
        setHubLoading(false);
      }
    } catch (error) {
      setHubLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setHubLoading(false);
    }
  };

  const handelDoubleSendOtp = async (value) => {
    setIsLoading(true);

    try {
      const payload = {
        username: loggedInUserName,
        bagNo: value?.bagNo,
        retailerId: value?.createdBy,
        deliveryBoyMobileNo: value?.deliverBoy?.mobileNo || "",
      };

      const response = await axiosPostApi(
        `${apiList.GENERATE_DUAL_OTP_HUB}`,
        payload,
      );
      const data = response?.data?.data;
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setDoubleOtpData(data);
        setIsLoading(false);
        setDoubleOtpModalOpen(true);
      } else {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      setIsLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handelDualVerifyOtp = async (vDOtpData) => {
    setLoading(true);

    try {
      const payload = {
        username: loggedInUserName,
        bagNo: columnData?.bagNo,
        firstUserOtp: vDOtpData?.sender,
        deliveryBoyOtp: vDOtpData?.receiver,
      };

      const response = await axiosPostApi(
        `${apiList.VERIFY_DUAL_OTP_HUB}`,
        payload,
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setOtpModalOpen(false);
        handelAcceptedByBoth();
      } else {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handelAcceptedByBoth = async () => {
    setLoading(true);

    try {
      const payload = {
        username: loggedInUserName,
        bagNo: columnData?.bagNo,
        retailerId: columnData?.createdBy,
        deliveryBoyMobileNo: columnData?.deliverBoy?.mobileNo,
      };

      let api = "";
      if (acceptHub) {
        api = apiList.CONFIRM_HUB_ARRIVAL;
      } else {
        api = apiList.HANDOVER_TO_DELIVERY_BOY;
      }

      const response = await axiosPostApi(api, payload);
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text: response?.data?.message,
          icon: "success",
        });
        setAcceptHub(false);
        fetchNotificationData(currentPage, pageSize, filterData);
      } else {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handelOpenBag = async (data) => {
    try {
      const payload = {
        username: loggedInUserName,
        bagNo: data?.bagNo,
        itemsIncluded: data?.itemsIncludedIds,
      };

      const response = await axiosPostApi(`${apiList.OPEN_BAG}`, payload);
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text: response?.data?.message,
          icon: "success",
        });
        fetchNotificationData(currentPage, pageSize, filterData);
      } else {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBox = (data) => {
    CustomAlert({
      title: "Info",
      text: `Are you sure you want to open Bag ${data?.bagNo}? This will update the bag status to "Opened at Hub"`,
      icon: "info",
      cancelButtonText: "Cancel",
      confirmButtonText: "Okay",
      showCancelButton: true,
      cancelButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        handelOpenBag(data);
      }
    });
  };

  const handleOtpSubmit = (data) => {
    handelVerifyOtp(data);
  };

  const handleDualOtpSubmit = (data) => {
    handelDualVerifyOtp(data);
  };

  const handleReset = () => {
    const resetFilterData = {
      bagNo: "",
      fromDate: getLast7DaysDate(),
      toDate: getTodayDate(),
      status: "",
    };
    setFilterData(resetFilterData);
    setCurrentPage(1);
    fetchNotificationData(1, pageSize, resetFilterData);
  };

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Notification Master" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchNotificationData(1, pageSize, next);
        }}
        search={{ key: "search", placeholder: "Search bag no., notification ID, sent by / to" }}
        selects={[{ key: "status", allLabel: "All statuses", chipLabel: "Status", options: NOTIFICATION_STATUS_OPTIONS }]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Sent" }}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
          {loading ? (
            <Loader />
          ) : (
            <>
              <MasterTable
                loading={loading}
                headers={customerHeader}
                data={data}
                handleAction={handleAction}
                filterFields={["bagNo"]}
                entityType="Notification"
                headerToKeyMap={customerHeaderToKeyMap}
                handleAuth={handleAuthorization}
                noData={noData}
                pageDetails={pageDetails}
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecord={totalRecord}
                fetchNextData={(page, size) =>
                  fetchNotificationData(page, size, filterData)
                }
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                openModalFilter={openModalFilter}
                handleReset={handleReset}
                filterData={filterData}
                fetchData={() =>
                  fetchNotificationData(1, pageSize, defaultFilterData)
                }
                hideFilterControls
              />
            </>
          )}
          {deliveryModalOpen && (
            <DeliveryBoyModal
              open={deliveryModalOpen}
              onClose={() => setDeliveryModalOpen(false)}
              onSubmit={handleDeliverySubmit}
              hubLoading={hubLoading}
            />
          )}

          <SingleOtpModal
            show={otpModalOpen}
            handleClose={() => setOtpModalOpen(false)}
            bagData={editData}
            onSubmit={handleOtpSubmit}
            data={otpData}
          />

          <AcceptOtpModal
            show={doubleOtpModalOpen}
            handleClose={() => {
              setDoubleOtpModalOpen(false);
              setAcceptHub(false);
            }}
            bagData={editData}
            onSubmit={handleDualOtpSubmit}
            data={doubleOtpData}
          />

          {isAddressDetailModalOpen && (
            <AddressDetailModal
              show={isAddressDetailModalOpen}
              handleClose={() => setIsAddressDetailModalOpen(false)}
              auditData={editData}
            />
          )}

          {isAddItemModalOpen ? (
            <AddItemBagModal
              show={isAddItemModalOpen}
              handleClose={() => setIsAddItemModalOpen(false)}
              editData={editData}
              action={modalAction}
              callApi={fetchOrderData}
              callTableApi={fetchNotificationData}
              columnData={columnData}
              headers={orderHeader}
              data={orderData}
              totalRecords={pageDetailsModal?.totalRecords || 0}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              loading={loadingModal}
              autoSelectAll={autoSelectAll}
              isViewMode={true}
            />
          ) : null}

          {isModalOpenFilter && (
            <NotificationMasterFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              setData={setData}
              setPageDetails={setPageDetails}
              setNoData={setNoData}
              pageSize={pageSize}
              currentPage={currentPage}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              setCurrentPage={setCurrentPage}
              fetchNotificationData={fetchNotificationData}
            />
          )}

          {openToNotifyModal && (
            <SelectToNotifyModal
              show={openToNotifyModal}
              handleClose={() => setOpenToNotifyModal(false)}
              columnData={columnData}
              franchiseData={{}}
              callApi={() => fetchNotificationData(currentPage)}
            />
          )}

        </div>
      </div>
    </>
  );
};

export default NotificationMaster;
